<template>
    <service-card
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-airplane"
        :is-first="isFirst"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :is-package="isPackage"
                :service-image="mainImage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :order="order"
                :order-end-date="orderEndDate"
                :not-itinerary-start="notItineraryStart"
                @cancel-service="$emit('cancel-service', $event)"
                @issue-ticket="$emit('issue-ticket', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <div v-if="service.status === 'Quote' && !isReadOnly" class="d-flex align-center">
                    <v-icon start size="small" color="primary" @click="fillFlightBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span class="text-caption cursor-pointer text-primary" @click="fillFlightBookingDataModal = true">
                        {{ $t('enter_details') }}
                    </span>
                    <fill-flight-booking-data-modal
                        v-model="fillFlightBookingDataModal"
                        :service="service"
                        :order="order"
                    />
                </div>
                <div
                    v-if="
                        service.serviceDetails[0].airlinesConfirmationNumbers &&
                        service.serviceDetails[0].airlinesConfirmationNumbers.length
                    "
                    class="order-info-base-info"
                >
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-ticket-confirmation-outline</v-icon>
                        <span class="text-body-2">{{ $t('flights.airline_pnr') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{
                            service.serviceDetails[0].airlinesConfirmationNumbers
                                .map(({airlineConfirmationNumber}) => airlineConfirmationNumber)
                                .join(', ')
                        }}
                    </span>
                </div>
                <template v-slot:additionalInfo2>
                    <flight-service-additional-info
                        v-if="isMinimized === false || infoLoadingFinished"
                        :service="service"
                    />
                    <v-btn
                        v-if="
                            service.serviceType !== 'OWNCHARTER' &&
                            $refs.baggageRules &&
                            $refs.baggageRules.baggageInfo.length
                        "
                        prepend-icon="mdi-information-outline"
                        variant="text"
                        @click="baggageRulesModal = true"
                    >
                        {{ $t('flights.fare_baggage_rules') }}
                    </v-btn>
                    <flight-baggage-rules ref="baggageRules" v-model="baggageRulesModal" :service="service" />
                </template>
                <template v-slot:belowImg>
                    <flight-operating-airlines :flight="flight" />
                </template>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog v-model="modal" :service="service" :is-info-loading="isInfoLoading">
                <template v-slot:serviceInfo>
                    <flight-itinerary
                        v-for="(itinerary, index) in itineraries"
                        :key="`itinerary-${index}`"
                        :itinerary="itinerary"
                    />
                    <detail-modal :offer="{processId, itinerary: itineraries}" />
                    <fare-rules :process-id="processId" />
                </template>
            </service-info-dialog>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Prop, Inject} from 'vue-facing-decorator'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import FlightItinerary from '@/components/flights/snippets/FlightItinerary.vue'
    import DetailModal from '~/components/flights/DetailModal.vue'
    import FareRules from '~/components/flights/FareRules.vue'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import {fullUrl} from '~src/utils/host-adapter'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FillFlightBookingDataModal from '~src/components/account/modals/newOrderRequest/fillFlightBookingDataModal.src'
    import FlightServiceAdditionalInfo from '~src/components/account/snippets/FlightServiceAdditionalInfo.vue'
    import FlightOperatingAirlines from '@/components/flights/snippets/FlightOperatingAirlines.vue'
    import FlightOrderServiceAdapter from '@/api/flights/adapter/FlightOrderServiceAdapter'
    import FlightBaggageRules from '@/components/flights/snippets/FlightBaggageRules.vue'

    @Component({
        components: {
            FlightBaggageRules,
            FlightOperatingAirlines,
            FlightServiceAdditionalInfo,
            FillFlightBookingDataModal,
            ServiceCard,
            FlightItinerary,
            OrderInfoBase,
            ServiceInfoDialog,
            DetailModal,
            FareRules,
        },
    })
    export class FlightService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() itineraryService
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        airports = {}
        itineraries = []
        modal = false
        fillFlightBookingDataModal = false
        baggageRulesModal = false

        showInfo() {
            if (!this.infoLoadingFinished) {
                this.loadProductInfo()
            }

            this.modal = true
        }

        async mounted() {}

        async loadProductInfo() {
            this.isInfoLoading = true

            try {
                await Promise.all([this.getAirportsData(), flightsStore.loadAirlines()])
            } catch (e) {
                /* empty */
            }

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        async getAirportsData() {
            let codes = new Set()
            this.service.serviceDetails[0].itineraries.forEach(itinerary => {
                itinerary.flightSegments.forEach(segment => {
                    codes.add(segment.arrivalAirportCode)
                    codes.add(segment.departureAirportCode)
                })
            })
            codes = Array.from(codes)
            const promises = []
            codes.forEach(code => {
                const rq = {
                    pattern: code,
                }
                promises.push(this.$api.airports.get(rq))
            })
            await Promise.all(promises).then(data => {
                data.forEach((response, index) => {
                    let airport = response.find(el => el.iataCode === codes[index] && el.type === 'airport')
                    if (!airport) {
                        airport = response.find(el => el.iataCode === codes[index])
                    }
                    this.airports[codes[index]] = airport
                })
            })
            this.getItineraries()
        }

        getItineraries() {
            const result = this.service.serviceDetails[0].itineraries.map(({duration, flightSegments}) => {
                const segments = flightSegments.map(
                    ({
                        flightSegmentName,
                        marketingAirlineName,
                        mealCode,
                        rph,
                        baggageInfo,
                        aircraftCode,
                        stopLocations,
                        departureAirportCode,
                        arrivalAirportCode,
                        categoryClass,
                        flightNumber,
                        stopQuantity,
                        arrivalDate,
                        departureDate,
                        aircraftName,
                        brandedFareName,
                        marketingAirline,
                    }) => {
                        const departure = this.airports[departureAirportCode] || {}
                        departure['airportName'] = departure['name']
                        const arrival = this.airports[arrivalAirportCode] || {}
                        arrival['airportName'] = arrival['name']

                        return {
                            name: flightSegmentName,
                            marketingAirlineName,
                            duration,
                            mealCode,
                            rph,
                            baggageInfo,
                            departure: {
                                airportDescription: this.airports[departureAirportCode],
                                airportCode: departureAirportCode,
                                date: departureDate,
                            },
                            arrival: {
                                airportDescription: this.airports[arrivalAirportCode],
                                airportCode: arrivalAirportCode,
                                date: arrivalDate,
                            },
                            aircraft: {
                                code: aircraftCode,
                                name: aircraftName,
                            },
                            stops: {
                                locations: stopLocations,
                                quantity: stopQuantity,
                            },
                            class: categoryClass,
                            flightNumber,
                            marketingAirline,
                            brandedFareName,
                        }
                    }
                )

                return {
                    duration,
                    segments,
                }
            })
            this.itineraries = result
        }

        get name() {
            return this.service.serviceDetails[0].flightName
        }

        get airportsLoaded() {
            return Object.keys(this.airports).length > 0
        }

        get processId() {
            return this.service.processId.replace(/^.{6}/, '')
        }

        get serviceCheckIn() {
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return `${this.$t('flights.departure')}: ${date}`
        }

        get serviceCheckOut() {
            if (
                this.service.serviceDetails[0].flightType === 'ONEWAY' ||
                this.service.serviceDetails[0].flightType === 'MULTICITY'
            ) {
                return null
            }
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `${this.$t('flights.return')}: ${date}`
        }

        get mainImage() {
            return fullUrl(
                `/stat/images/airlines/${this.service.serviceDetails[0].itineraries[0].flightSegments[0].marketingAirline}.gif`
            )
        }

        get notItineraryStart() {
            return this.itineraryService?.type?.start === false
        }

        get isTO1() {
            return authStore.isTO1
        }

        get flight() {
            return FlightOrderServiceAdapter(this.service)
        }

        get ownProduct() {
            return runtimeStore.ownProduct(this.service.supplierCode)
        }
    }

    export default FlightService
</script>

<style lang="scss" scoped>
    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .modal-close-btn-block {
        position: relative;
        top: 12px;
        right: 12px;
    }
</style>
